import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import {
  TrendingUp,
  GraduationCap,
  Coins,
  BarChart3,
  ArrowLeftRight,
  Zap,
  ArrowRight,
  Calculator,
  Scale,
  DollarSign,
  Percent,
  PieChart,
  Receipt,
  Wallet,
  Landmark,
  BookOpen,
  Puzzle,
  Calendar,
  Target,
  Gamepad2,
  Flame,
  Sprout,
  AlertTriangle,
  Layers,
  LineChart,
  type LucideIcon,
} from 'lucide-react';
import {
  BLOG_ARTICLES,
  CATEGORY_LABELS,
  type BlogCategory,
} from '../../config/blogArticles';

const ICONS: Record<string, LucideIcon> = {
  TrendingUp,
  GraduationCap,
  Coins,
  BarChart3,
  ArrowLeftRight,
  Zap,
  Calculator,
  Scale,
  DollarSign,
  Percent,
  PieChart,
  Receipt,
  Wallet,
  Landmark,
  BookOpen,
  Puzzle,
  Calendar,
  Target,
  Gamepad2,
  Flame,
  Sprout,
  AlertTriangle,
  Layers,
  LineChart,
};

type FilterValue = 'all' | BlogCategory;

const FILTERS: Array<{ value: FilterValue; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'concept', label: CATEGORY_LABELS.concept },
  { value: 'game', label: CATEGORY_LABELS.game },
  { value: 'guide', label: CATEGORY_LABELS.guide },
];

export default function BlogIndexPage() {
  const [filter, setFilter] = useState<FilterValue>('all');

  const visibleArticles = useMemo(() => {
    if (filter === 'all') return BLOG_ARTICLES;
    return BLOG_ARTICLES.filter((a) => a.category === filter);
  }, [filter]);

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Wallstreetle Blog: Investing Education & Stock Market Guides',
    description:
      "Articles on investing, stock picking, paper trading, and financial literacy. Learn to invest with Wallstreetle's interactive stock puzzles.",
    url: 'https://wallstreetle.com/blog',
    publisher: {
      '@type': 'Organization',
      name: 'Wallstreetle',
      url: 'https://wallstreetle.com',
    },
    blogPost: BLOG_ARTICLES.map((article) => ({
      '@type': 'BlogPosting',
      headline: article.title,
      url: `https://wallstreetle.com/blog/${article.slug}`,
      description: article.excerpt,
      datePublished: article.datePublished,
    })),
  };

  return (
    <>
      <Helmet>
        <title>Wallstreetle | Investing Blog</title>
        <meta
          name="description"
          content="Articles on how to invest, stock picking strategies, paper trading, and financial literacy. Learn investing through interactive stock puzzles at Wallstreetle."
        />
        <meta
          name="keywords"
          content="how to invest, learn to invest, paper trading, stock picking, investing for beginners, stock market education, financial literacy, investing guide, stock wordle, finance wordle"
        />
        <link rel="canonical" href="https://wallstreetle.com/blog" />

        <meta property="og:title" content="Wallstreetle | Investing Blog" />
        <meta property="og:description" content="Articles on investing, stock picking, paper trading, and financial literacy from Wallstreetle." />
        <meta property="og:url" content="https://wallstreetle.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Wallstreetle | Investing Blog" />
        <meta property="twitter:description" content="Articles on investing, stock picking, paper trading, and financial literacy." />
        <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto py-12 px-4">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-4">
            Wallstreetle Blog
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Guides and insights to help you learn investing, understand the stock market, and build confidence with your money.
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Filter articles by category"
          className="flex flex-wrap justify-center gap-2 mb-10"
        >
          {FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(f.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-muted-foreground border-border hover:text-foreground hover:border-foreground/30'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {visibleArticles.map((article) => {
            const Icon = ICONS[article.iconName] ?? TrendingUp;
            return (
              <Link key={article.slug} to={`/blog/${article.slug}`} className="group">
                <Card className="h-full p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg shrink-0 ${article.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">
                        {CATEGORY_LABELS[article.category]}
                      </div>
                      <h2 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-muted-foreground mb-3">
                        {article.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                        Read article <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>

        <div className="mt-16">
          <Card className="bg-gradient-to-r from-primary to-primary/80 text-white p-8 sm:p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-3">
              Ready to put your knowledge to work?
            </h2>
            <p className="text-white/90 mb-6 max-w-xl mx-auto">
              Play Wallstreetle's daily stock puzzles and test your investing instincts with real financial data.
            </p>
            <Link
              to="/start"
              className="inline-block bg-white text-primary font-semibold px-8 py-3 rounded-lg hover:bg-white/90 transition-colors"
            >
              Play Now
            </Link>
          </Card>
        </div>
      </div>
    </>
  );
}
